import React from 'react';

import { Caption } from '@depop/web-thrift-ui/Typography';
import { InfoIcon } from '@depop/web-ui-kit/Icons/InfoIcon';
import { RadioButtons, RadioLayout } from '@depop/web-ui-kit/RadioButtons';
import { Tooltip } from '@depop/web-ui-kit/Tooltip';
import { useQueryClient } from '@tanstack/react-query';

import styles from './styles.module.css';

import { RQ_PREVIEW_EXPS_CACHE } from '@/modules/ReactQuery/cacheKeys';
import {
  OPTIMIZELY_EXPERIMENTS,
  type OpzlyExperimentName,
} from '@/modules/experiments/config';
import { getForcedVariantExperiments } from '@/modules/experiments/helpers/getForcedVariantExperiments';
import { isExperimentOption } from '@/modules/experiments/helpers/isExperimentOption.ts';
import { setForcedVariantExperiments } from '@/modules/experiments/helpers/setForcedVariantExperiments';
import { type CachedExperiments } from '@/modules/experiments/types';

export function ExperimentSwitcherRow({
  name,
  selectedVariant,
}: {
  name: string;
  selectedVariant: string;
}) {
  const client = useQueryClient();
  const [newValue, setNewValue] = React.useState('');

  const configuredVariants = Object.entries(
    OPTIMIZELY_EXPERIMENTS[name as OpzlyExperimentName]
  )
    .filter(([label]) => isExperimentOption(label))
    .map(([label, value]) => ({
      id: `radio-id-${name}-${label}`,
      labelText: label,
      value: String(value),
    }));

  async function onChangeVariant(e: React.ChangeEvent<HTMLInputElement>) {
    const { value } = e.target;

    let forcedExperiments: string | undefined =
      await getForcedVariantExperiments();

    // Ensures we don't end up with duplicate entries
    if (forcedExperiments) {
      forcedExperiments = forcedExperiments
        .split(',')
        .map((exp) => exp.split(':'))
        .filter(([key]) => key !== name)
        .map(([key, value]) => `${key}:${value}`)
        .join(',');
    }

    const experimentsToOverride = [
      ...(forcedExperiments ? forcedExperiments?.split(',') : []),
      `${name}:${value}`,
    ];

    // Set cookie so the changes persist on reload
    setForcedVariantExperiments(experimentsToOverride);

    // Update the cache with the overridden variant so we get an instant change
    client.setQueryData(
      [RQ_PREVIEW_EXPS_CACHE],
      (cached: CachedExperiments | undefined) => {
        const updateableCopy = {
          ...cached,
        };

        for (const exp of experimentsToOverride) {
          const [expName, decision] = exp.split(':');

          if (updateableCopy[expName]) {
            updateableCopy[expName] = {
              ...updateableCopy[expName],
              decision,
            };
          }
        }

        return updateableCopy;
      }
    );

    setNewValue(value);
  }

  const invalidValue =
    configuredVariants.length > 0 &&
    configuredVariants.every(
      ({ value }) => value !== (newValue || selectedVariant)
    );

  return (
    <tr>
      <td>
        <div className={styles.expTitleRow}>
          <Caption variant="lg-regular">{name}</Caption>
          {invalidValue && (
            <Tooltip
              text={`Expected: ${configuredVariants.map((variant) => variant.value).join(' | ')}, received: ${newValue || selectedVariant}`}
            >
              <InfoIcon />
            </Tooltip>
          )}
        </div>
        <RadioButtons
          activeValue={newValue || selectedVariant}
          layout={RadioLayout.inline}
          name={`exp-switcher-radios-${name}`}
          onChange={onChangeVariant}
          options={configuredVariants}
        />
      </td>
    </tr>
  );
}
